import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Search, Tag, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPricing = () => {
  const [rules, setRules] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [form, setForm] = useState({ company: '', product_id: '', custom_price: '' });
  const [companyMode, setCompanyMode] = useState('existing'); // existing | new
  const [newCompany, setNewCompany] = useState('');

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rulesRes, compRes, prodRes] = await Promise.all([
        axios.get(`${API}/admin/pricing`, auth()),
        axios.get(`${API}/admin/companies`, auth()),
        axios.get(`${API}/admin/products?limit=1000`, auth()),
      ]);
      setRules(rulesRes.data || []);
      setCompanies(compRes.data || []);
      setProducts(prodRes.data || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to load pricing data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    const company = (companyMode === 'new' ? newCompany : form.company).trim();
    if (!company) return toast({ title: 'Missing company', description: 'Select or enter a company.', variant: 'destructive' });
    if (!form.product_id) return toast({ title: 'Missing product', description: 'Select a product.', variant: 'destructive' });
    const price = parseFloat(form.custom_price);
    if (isNaN(price) || price < 0) return toast({ title: 'Invalid price', variant: 'destructive' });

    try {
      await axios.post(`${API}/admin/pricing`, { company, product_id: form.product_id, custom_price: price }, auth());
      toast({ title: 'Saved', description: `Contract price set for ${company}` });
      setIsAddOpen(false);
      setForm({ company: '', product_id: '', custom_price: '' });
      setNewCompany('');
      setCompanyMode('existing');
      fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to save rule', variant: 'destructive' });
    }
  };

  const handleDelete = async (company, product_id, label) => {
    if (!window.confirm(`Remove contract price for "${label}"?`)) return;
    try {
      await axios.delete(`${API}/admin/pricing/${encodeURIComponent(company)}/${encodeURIComponent(product_id)}`, auth());
      toast({ title: 'Removed' });
      fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to remove', variant: 'destructive' });
    }
  };

  // Group rules by company
  const grouped = rules
    .filter(r => !search || r.company.includes(search.toLowerCase()) || (r.product_name || '').toLowerCase().includes(search.toLowerCase()))
    .reduce((acc, r) => {
      acc[r.company] = acc[r.company] || [];
      acc[r.company].push(r);
      return acc;
    }, {});

  return (
    <div className="p-4 sm:p-8" data-testid="admin-pricing-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-800 flex items-center gap-2">
            <Tag className="h-7 w-7 text-purple-600" /> Custom Pricing Rules
          </h1>
          <p className="text-sm text-slate-500 mt-1">Set contract prices per organization (company) and product. Buyers from that organization see these prices automatically.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setIsUploadOpen(true); setUploadFile(null); setUploadResult(null); }} data-testid="bulk-upload-pricing-btn">
            <Upload className="h-4 w-4 mr-1" /> Bulk Upload
          </Button>
          <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => setIsAddOpen(true)} data-testid="add-pricing-rule-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Rule
          </Button>
        </div>
      </div>

      <Card className="mb-6 border-2">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by company or product..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="pricing-search"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div></div>
      ) : Object.keys(grouped).length === 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="p-8 text-center text-slate-500" data-testid="no-pricing-rules">
            No custom pricing rules yet. Click "Add Rule" to create your first contract price.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([company, list]) => (
            <Card key={company} className="border-2" data-testid={`pricing-company-${company}`}>
              <CardHeader>
                <CardTitle className="capitalize text-lg">{company} <span className="text-sm font-normal text-slate-400">— {list.length} rule(s)</span></CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3">Product</th>
                        <th className="text-left p-3">Default Price</th>
                        <th className="text-left p-3">Contract Price</th>
                        <th className="text-left p-3">Discount</th>
                        <th className="text-left p-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map(r => {
                        const def = Number(r.product_default_price || 0);
                        const cust = Number(r.custom_price || 0);
                        const discount = def > 0 ? Math.round(((def - cust) / def) * 100) : 0;
                        return (
                          <tr key={r.product_id} className="border-t hover:bg-slate-50" data-testid={`pricing-rule-${company}-${r.product_id}`}>
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                {r.product_image ? <img src={r.product_image} alt="" className="w-10 h-10 rounded object-cover" /> : null}
                                <span className="font-medium">{r.product_name || r.product_id}</span>
                              </div>
                            </td>
                            <td className="p-3 text-slate-600">${def.toFixed(2)}</td>
                            <td className="p-3 font-semibold text-emerald-700">${cust.toFixed(2)}</td>
                            <td className="p-3">
                              {discount > 0 ? <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs">-{discount}%</span> : <span className="text-slate-400 text-xs">—</span>}
                            </td>
                            <td className="p-3">
                              <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(company, r.product_id, r.product_name)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Bulk Upload Dialog */}
      <Dialog open={isUploadOpen} onOpenChange={(o) => { if (!o) { setIsUploadOpen(false); setUploadFile(null); setUploadResult(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Bulk Upload Pricing Rules</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
              <p className="font-semibold mb-2">Required columns (any product identifier accepted):</p>
              <ul className="list-disc list-inside text-xs space-y-0.5">
                <li><b>company</b> — organization name (e.g. "Acme Corp")</li>
                <li><b>custom_price</b> (or "price" / "contract_price") — contract price in USD</li>
                <li>One of <b>product_id</b>, <b>sku</b>, or <b>product_name</b> — to identify the product</li>
              </ul>
              <p className="text-xs mt-2 text-blue-700">CSV or Excel (.xlsx, .xls). Existing rules for the same company+product will be overwritten.</p>
            </div>

            <div>
              <Label htmlFor="bulk-pricing-file">Select CSV or Excel file *</Label>
              <Input
                id="bulk-pricing-file"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="pricing-upload-file"
              />
              {uploadFile && <p className="text-xs text-slate-500 mt-1">Selected: {uploadFile.name}</p>}
            </div>

            {uploadResult && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs" data-testid="pricing-upload-result">
                <p className="font-semibold text-emerald-800">{uploadResult.message}</p>
                <p className="text-slate-600 mt-1">
                  Inserted: {uploadResult.inserted} · Updated: {uploadResult.updated} · Skipped: {uploadResult.skipped} · Total rows: {uploadResult.total_rows}
                </p>
                {uploadResult.errors?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-amber-700">{uploadResult.errors.length} warning(s)</summary>
                    <ul className="list-disc list-inside text-amber-700 mt-1">
                      {uploadResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setIsUploadOpen(false); setUploadFile(null); setUploadResult(null); }}>Close</Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={async () => {
                  if (!uploadFile) return toast({ title: 'Pick a file first', variant: 'destructive' });
                  setUploading(true);
                  setUploadResult(null);
                  try {
                    const fd = new FormData();
                    fd.append('file', uploadFile);
                    const res = await axios.post(`${API}/admin/pricing/upload`, fd, {
                      headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' }
                    });
                    setUploadResult(res.data);
                    toast({ title: 'Upload complete', description: res.data.message });
                    fetchAll();
                  } catch (e) {
                    toast({ title: 'Upload failed', description: e.response?.data?.detail || 'Could not parse file', variant: 'destructive' });
                  } finally {
                    setUploading(false);
                  }
                }}
                disabled={uploading || !uploadFile}
                data-testid="pricing-upload-submit"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Custom Pricing Rule</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Organization</Label>
              <div className="flex gap-2 mt-1 mb-2 text-xs">
                <button type="button" className={`px-2 py-1 rounded ${companyMode === 'existing' ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600'}`} onClick={() => setCompanyMode('existing')}>From registered users</button>
                <button type="button" className={`px-2 py-1 rounded ${companyMode === 'new' ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600'}`} onClick={() => setCompanyMode('new')}>Type new</button>
              </div>
              {companyMode === 'existing' ? (
                <Select value={form.company} onValueChange={(v) => setForm({ ...form, company: v })}>
                  <SelectTrigger data-testid="select-company"><SelectValue placeholder="Select a company" /></SelectTrigger>
                  <SelectContent>
                    {companies.length === 0 && <SelectItem value="__none" disabled>No companies registered yet</SelectItem>}
                    {companies.map(c => (
                      <SelectItem key={c.company_normalized} value={c.company_normalized}>{c.company}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input placeholder="e.g. Acme Corp" value={newCompany} onChange={(e) => setNewCompany(e.target.value)} data-testid="input-new-company" />
              )}
            </div>
            <div>
              <Label>Product</Label>
              <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                <SelectTrigger data-testid="select-product"><SelectValue placeholder="Select a product" /></SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} (${p.price})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contract Price ($)</Label>
              <Input type="number" min="0" step="0.01" value={form.custom_price} onChange={(e) => setForm({ ...form, custom_price: e.target.value })} placeholder="e.g. 14.99" data-testid="input-custom-price" />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleAdd} data-testid="save-pricing-rule-btn">Save Rule</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPricing;
